// IVA mensual (DGI) — débito sobre ventas, crédito sobre compras a proveedores.
// Limpack vende con IVA incluido en precio (22% UY).

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'

const IVA_PCT = 0.22
const MESES_ATRAS = 12

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const ymKey = (d) => {
  const dt = new Date(d)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
}
const ymLabel = (ym) => {
  const [y, m] = ym.split('-')
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Set','Oct','Nov','Dic']
  return `${meses[Number(m) - 1]} ${y.slice(2)}`
}
// IVA en precio incluido: 22% se descompone como  monto * 22/122
const ivaDesdeBruto = (n) => Number(n || 0) * (IVA_PCT / (1 + IVA_PCT))

export default function Iva() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagos, setPagos] = useState([])
  const [pedidos, setPedidos] = useState([])
  const [compras, setCompras] = useState([])

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      const desde = new Date()
      desde.setMonth(desde.getMonth() - MESES_ATRAS)
      desde.setDate(1)
      const desdeStr = desde.toISOString().slice(0, 10)

      const [{ data: pa, error: pe }, { data: pd, error: pde }, { data: mv, error: mve }] = await Promise.all([
        // IVA/DGI: solo pagos y pedidos reales (nunca es_prueba)
        supabase.from('pagos').select('monto, pagado_at, metodo').eq('estado', 'pagado').eq('es_prueba', false).gte('pagado_at', desdeStr),
        supabase.from('pedidos').select('monto_total, pagado_at, metodo_pago, created_at').in('estado', ['pagado', 'en_armado', 'en_ruta', 'entregado']).eq('es_prueba', false).gte('created_at', desdeStr),
        // Compras a proveedores → movimientos_stock con tipo 'compra' o 'entrada'
        supabase.from('movimientos_stock').select('cantidad, costo_unitario, tipo, created_at').gte('created_at', desdeStr),
      ])
      if (pe) throw pe
      if (pde) throw pde
      if (mve) throw mve
      setPagos(pa || [])
      setPedidos(pd || [])
      setCompras((mv || []).filter(m => ['compra', 'entrada', 'ingreso'].includes((m.tipo || '').toLowerCase())))
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  const filas = useMemo(() => {
    const map = new Map()
    function ensure(ym) {
      if (!map.has(ym)) map.set(ym, { ym, ventas: 0, ventas_susc: 0, ventas_oneshot: 0, compras: 0, iva_debito: 0, iva_credito: 0, saldo: 0 })
      return map.get(ym)
    }
    pagos.forEach(p => {
      const ym = ymKey(p.pagado_at)
      const r = ensure(ym)
      r.ventas_susc += Number(p.monto || 0)
    })
    pedidos.forEach(p => {
      const ym = ymKey(p.pagado_at || p.created_at)
      const r = ensure(ym)
      r.ventas_oneshot += Number(p.monto_total || 0)
    })
    compras.forEach(c => {
      const ym = ymKey(c.created_at)
      const r = ensure(ym)
      const monto = Number(c.cantidad || 0) * Number(c.costo_unitario || 0)
      r.compras += monto
    })

    map.forEach(r => {
      r.ventas = r.ventas_susc + r.ventas_oneshot
      r.iva_debito = ivaDesdeBruto(r.ventas)
      r.iva_credito = ivaDesdeBruto(r.compras)
      r.saldo = r.iva_debito - r.iva_credito
    })

    return Array.from(map.values()).sort((a, b) => b.ym.localeCompare(a.ym))
  }, [pagos, pedidos, compras])

  const totales = useMemo(() => {
    const t = { ventas: 0, compras: 0, iva_debito: 0, iva_credito: 0, saldo: 0 }
    filas.forEach(r => {
      t.ventas += r.ventas
      t.compras += r.compras
      t.iva_debito += r.iva_debito
      t.iva_credito += r.iva_credito
    })
    t.saldo = t.iva_debito - t.iva_credito
    return t
  }, [filas])

  return (
    <AdminLayout>
      <div className="page">
        <h1>🧾 IVA mensual (DGI)</h1>
        <p className="muted">Débito (sobre ventas) menos Crédito (sobre compras a proveedores) = saldo a pagar/recuperar a la DGI.</p>

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Ventas (incluye IVA)</h3>
            <div className="stat" style={{ color: '#10B981' }}>{fmtUYU(totales.ventas)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>últimos {MESES_ATRAS} meses</div>
          </div>
          <div className="card">
            <h3>Compras (incluye IVA)</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>{fmtUYU(totales.compras)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>a proveedores</div>
          </div>
          <div className="card">
            <h3>IVA débito (ventas)</h3>
            <div className="stat" style={{ color: '#f59e0b' }}>{fmtUYU(totales.iva_debito)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>22% sobre ventas</div>
          </div>
          <div className="card">
            <h3>Saldo IVA acumulado</h3>
            <div className="stat" style={{ color: totales.saldo >= 0 ? '#ef4444' : '#10B981' }}>
              {totales.saldo >= 0 ? '−' : '+'}{fmtUYU(Math.abs(totales.saldo))}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              {totales.saldo >= 0 ? 'a pagar a DGI' : 'a favor (crédito)'}
            </div>
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && filas.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>🧾</div>
            <h3>Sin datos en los últimos {MESES_ATRAS} meses</h3>
          </div>
        )}

        {!loading && filas.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="usu-table">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th style={{ textAlign: 'right' }}>Ventas (con IVA)</th>
                  <th style={{ textAlign: 'right' }}>IVA débito</th>
                  <th style={{ textAlign: 'right' }}>Compras (con IVA)</th>
                  <th style={{ textAlign: 'right' }}>IVA crédito</th>
                  <th style={{ textAlign: 'right' }}>Saldo a pagar</th>
                </tr>
              </thead>
              <tbody>
                {filas.map(r => (
                  <tr key={r.ym}>
                    <td style={{ fontWeight: 700, color: '#1a3461' }}>{ymLabel(r.ym)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUYU(r.ventas)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#f59e0b' }}>+{fmtUYU(r.iva_debito)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUYU(r.compras)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#10B981' }}>−{fmtUYU(r.iva_credito)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: r.saldo >= 0 ? '#ef4444' : '#10B981' }}>
                      {r.saldo >= 0 ? fmtUYU(r.saldo) : `(${fmtUYU(Math.abs(r.saldo))})`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AnalisisIA
          reportType="iva"
          data={{ totales, serie: filas.slice(0, 6) }}
        />

        <div style={{ marginTop: 20, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f' }}>
          <strong>📌 Notas:</strong> IVA UY 22%. Cálculo: el monto bruto incluye IVA → IVA = monto × 22/122.
          Las compras toman movimientos de stock tipo "compra/entrada/ingreso". El crédito real depende de las facturas que tengas como respaldo —
          consultá con el contador antes de presentar.
        </div>
      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th { text-align: left; padding: 12px 14px; background: #f8fafc; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-transform: uppercase; color: #6b7280; font-weight: 700; letter-spacing: .03em; }
        .usu-table tbody td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
      `}</style>
    </AdminLayout>
  )
}
